import React, { useState } from "react";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSubTableContainer,
} from "@/common/styles";
import { BookingsSectionTitle } from "./BookingsDetails/styles";
import Table from "@/common/elements/Table";
import CustomModal from "./CustomModal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import TextField from "@/common/elements/InputElements/TextField";
import Button from "@/common/elements/Button";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getOptionsFromRefList } from "@/util/common_function";
import { Form } from "antd";
import notificationService from "@/util/notificationService"; // or use window.alert

const ScheduledEventsInspections = ({ inspections = [], setInspections }) => {
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  // Local state: show/hide modal + selected row
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // AntD form instance
  const [form] = Form.useForm();

  const inspectorOptions = getOptionsFromRefList("InspectorList", true, false, true);

  // Helper function to get inspector name from inspectorStaffId
  const getInspectorName = (id) => {
    const option = inspectorOptions.find((opt) => opt.value === id);
    return option ? option.label : id;
  };

  // Convert inspections to table data, displaying inspector names
  const tableData = inspections.map((insp, idx) => {
    let dateStr = "";
    let timeStr = "";
    if (insp.actualDate && insp.actualTime) {
      const dt = dayjs(`${insp.actualDate} ${insp.actualTime}`, "MM/DD/YYYY HH:mm");
      if (dt.isValid()) {
        dateStr = dt.format("MM/DD/YYYY"); 
        timeStr = dt.format("HH:mm");
      }
    }
    return {
      key: String(idx), // unique key as string
      inspector: getInspectorName(insp.inspectorStaffId) || "",
      actualDate: dateStr,
      actualTime: timeStr,
      problem: insp.problem ? "True" : "False",
      comments: insp.comments || "",
      _original: insp,
    };
  });

  // Handle Add button click
  const handleAdd = () => {
    setSelectedRow(null); // Clear any selected row
    form.setFieldsValue({
      inspectorStaffId: userDetails?.staffId || null,
      actualDateTime: dayjs() || null,
      problem: false,
      comments: "",
    });
    setShowModal(true);
  };

  // Handle Details button click (Edit)
  const handleDetails = () => {
    if (!selectedRow) {
      notificationService.warning("No row selected.");
      return;
    }
    const rowData = selectedRow._original;

    // Combine actualDate and actualTime into a single dayjs object for the date picker
    let actualDateTimeVal = null;
    if (rowData.actualDate && rowData.actualTime) {
      actualDateTimeVal = dayjs(`${rowData.actualDate} ${rowData.actualTime}`, "MM/DD/YYYY HH:mm");
      if (!actualDateTimeVal.isValid()) {
        actualDateTimeVal = null;
      }
    }

    // Fill the form with the row data
    form.setFieldsValue({
      inspectorStaffId: rowData.inspectorStaffId || userDetails?.staffId,
      actualDateTime: actualDateTimeVal || dayjs(),
      problem: !!rowData.problem,
      comments: rowData.comments || "",
    });

    setShowModal(true);
  };

  // Handle Delete button click
  const handleDelete = () => {
    if (!selectedRow) {
      notificationService.warning("No row selected.");
      return;
    }
    const idx = parseInt(selectedRow.key, 10);
    const updated = inspections.filter((_, i) => i !== idx);
    setInspections(updated);
    setSelectedRow(null);
  };

  // Handle Save button click inside modal
  const handleSaveInspection = async () => {
    try {
      // Validate and get form fields
      const values = await form.validateFields();

      // Split actualDateTime into actualDate and actualTime
      const actualDate = values.actualDateTime
        ? dayjs(values.actualDateTime).format("MM/DD/YYYY")
        : null
      const actualTime = values.actualDateTime
        ? dayjs(values.actualDateTime).format("HH:mm")
        : null;

      const newInspection = {
        inspectorStaffId: values.inspectorStaffId || "",
        actualDate: actualDate,
        actualTime: actualTime,
        problem: !!values.problem,
        comments: values.comments || "",
      };

      if (selectedRow && selectedRow._original) {
        // Editing existing inspection
        const editIndex = parseInt(selectedRow.key, 10);
        const updated = [...inspections];
        updated[editIndex] = newInspection;
        setInspections(updated);
      } else {
        // Adding new inspection
        setInspections([...inspections, newInspection]);
      }

      // Close modal and reset states
      setShowModal(false);
      setSelectedRow(null);
      form.resetFields();
    } catch (err) {
      console.error("Form validation error:", err);
    }
  };

  return (
    <>
      <BookingsSectionTitle>
        <span>Inspections</span>
      </BookingsSectionTitle>

      <StyledSubTableContainer>
        <StyledFlex justify="end" gap="10px" marginBottom="10px">
          <Button icon={<PlusOutlined />} onClick={handleAdd}>
            Add
          </Button>
          <Button disabled={!selectedRow} onClick={handleDetails}>Details</Button>
          <Button disabled={!selectedRow}  onClick={handleDelete}>Delete</Button>
        </StyledFlex>

        <Table
          selected={!selectedRow}
          headers={[
            { key: "inspector", text: "Inspector" },
            { key: "actualDate", text: "Actual Date" },
            { key: "actualTime", text: "Actual Time" },
            { key: "problem", text: "Problem" },
            { key: "comments", text: "Comments" },
          ]}
          data={tableData}
          // variant="outlined"
          // Handle row click to select a row
          onRowClick={(row) => setSelectedRow(row)}
        />
      </StyledSubTableContainer>

      {showModal && (
        <CustomModal
          show={showModal}
          header="Inspection Details"
          onHide={() => setShowModal(false)}
          size="lg"
          onSaveClick={handleSaveInspection}
          backdrop={false}
        >
          <Form
            form={form}
            name="ScheduledEventsInspectionsForm"
            layout="vertical"
          >
            <StyledGridRow gutter={16}>
              <StyledGridCol lg={8}>
                <FormItem
                  label="Inspector"
                  name="inspectorStaffId"
                >
                  <Select
                    options={inspectorOptions}
                    placeholder="Select Inspector"
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <FormItem
                  label="Actual Date/Time"
                  name="actualDateTime"
                >
                  <CommonDatePicker
                    format="MM/DD/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <FormItem layout="horizontal" labelPostion="label-right"
                  paddingTop="24px"
                  label="Problem?"
                  name="problem"
                  valuePropName="checked"
                >
                  <Checkbox />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={24}>
                <FormItem label="Comments" name="comments">
                  <TextField inputType="textarea" rows="4" />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
          </Form>
        </CustomModal>
      )}

    </>
  );
};

export default ScheduledEventsInspections;
